/**
 * AuthLayout — the split-screen frame shared by sign-in and sign-up. Left is the
 * court-exhibit AuthBrandPanel (desktop only); right holds the title, the Clerk
 * form (passed as children), the cross-link, and the "Secured by Clerk" line.
 * Square geometry + token-only styling to match the homepage. Clerk-free so the
 * frame renders in the harness; the page wraps it around the actual Clerk widget.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FigureCoWordmark } from '../brand/FigureCoLogo';
import AuthBrandPanel from './AuthBrandPanel';

interface AuthLayoutProps {
  title: string;
  subtitle: string;
  children: ReactNode;
  /** Secondary cross-link under the form. Omit for flows with no alt action. */
  altPrompt?: string;
  altLinkText?: string;
  altLinkTo?: string;
  /** Wrap children in the hairline mount box (for the embedded Clerk widget).
   *  Hand-built forms set this false and render their own fields directly. */
  framed?: boolean;
  /** Optional extra content under the cross-link (e.g. a back-to-site link). */
  footerExtra?: ReactNode;
  /** The ruled footer note. Clerk pages pass the "Secured by Clerk" variant. */
  securedNote?: string;
}

export default function AuthLayout({
  title,
  subtitle,
  children,
  altPrompt,
  altLinkText,
  altLinkTo,
  framed = true,
  footerExtra,
  securedNote = 'Encrypted in transit',
}: AuthLayoutProps) {
  return (
    <div className="flex min-h-screen bg-base font-body">
      {/* Left — exhibit panel (desktop) */}
      <div className="hidden lg:block lg:w-1/2 xl:w-[55%]">
        <AuthBrandPanel />
      </div>

      {/* Right — the form. Top-anchored content (so the column doesn't jump between
          routes of different heights) with the secured note pinned to the bottom,
          so its baseline lines up with the left panel's © footer. Vertical padding
          matches the left panel (p-12) for that alignment. */}
      <div className="flex min-h-screen w-full flex-col px-8 py-12 lg:w-1/2 lg:px-12 xl:w-[45%]">
        <div className="mx-auto w-full max-w-md">
          {/* Mobile wordmark */}
          <div className="mb-10 lg:hidden">
            <FigureCoWordmark size="lg" />
          </div>

          <div className="mb-8">
            <h2 className="font-heading text-3xl leading-tight tracking-tight text-text-primary">
              {title}
            </h2>
            <p className="mt-2 font-body text-sm text-text-secondary">{subtitle}</p>
          </div>

          {/* Embedded widget gets the hairline mount box; hand-built forms render raw. */}
          {framed ? <div className="border border-hairline p-1">{children}</div> : children}

          {altPrompt && altLinkTo && altLinkText && (
            <p className="mt-6 font-body text-sm text-text-secondary">
              {altPrompt}{' '}
              <Link
                to={altLinkTo}
                className="font-mono text-xs uppercase tracking-[0.12em] text-text-primary underline decoration-[var(--color-border-strong)] underline-offset-4 transition-colors hover:text-brand-text hover:decoration-current"
              >
                {altLinkText}
              </Link>
            </p>
          )}

          {footerExtra}
        </div>

        <p className="mx-auto mt-auto w-full max-w-md pt-10 font-mono text-[11px] uppercase tracking-[0.14em] text-text-dim">
          {securedNote}
        </p>
      </div>
    </div>
  );
}
